import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { Loading, ErrorState } from '../../components/ui';
import { Panel } from '../../components/charts';
import { METHOD_LABEL } from './Dashboard';

/** Membuat analisis: metode yang datanya belum ada tidak bisa dipilih. */
type MethodRow = {
  method: string;
  needsBaseline: boolean;
  needsAsBuilt: boolean;
  strength: string;
  limitations: string;
  readiness: { ok: boolean; missing: string[] };
};

type VersionRow = { id: string; label: string; dataDate: string; isBaseline: boolean; status: string };

export function NewAnalysis({ projectId }: { projectId: string }) {
  const nav = useNavigate();
  const [methods, setMethods] = useState<MethodRow[] | null>(null);
  const [versions, setVersions] = useState<VersionRow[]>([]);
  const [title, setTitle] = useState('');
  const [method, setMethod] = useState('');
  const [baselineVersionId, setBaseline] = useState('');
  const [asBuiltVersionId, setAsBuilt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, v] = await Promise.all([
        api.get<{ methods: MethodRow[] }>(`/delay/methods?projectId=${projectId}`),
        api.get<{ versions: VersionRow[] }>(`/programme/versions?projectId=${projectId}`),
      ]);
      setMethods(r.methods);
      const accepted = v.versions.filter((x) => x.status === 'ACCEPTED');
      setVersions(accepted);
      setMethod((m) => m || (r.methods.find((x) => x.readiness.ok)?.method ?? ''));
      // Bawaan yang masuk akal: baseline yang ditandai, dan versi
      // terakhir sebagai as-built. Keduanya tetap bisa diganti —
      // analis yang memutuskan programme mana yang dibandingkan.
      setBaseline((b) => b || (accepted.find((x) => x.isBaseline)?.id ?? accepted[0]?.id ?? ''));
      setAsBuilt((a) => a || (accepted.at(-1)?.id ?? ''));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load methods.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !method) return;
    setBusy(true);
    try {
      const r = await api.post<{ analysisId: string }>('/delay/analyses', {
        projectId,
        title: title.trim(),
        method,
        baselineVersionId: baselineVersionId || null,
        asBuiltVersionId: asBuiltVersionId || null,
      });
      nav(`/analyses/${r.analysisId}`);
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'Gagal membuat analisis.');
    } finally {
      setBusy(false);
    }
  }

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!methods) return <Loading rows={4} />;

  const chosen = methods.find((m) => m.method === method);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Analisis baru</h1>
          <p>Metode dipilih di sini dan tercatat pada laporannya, bersama kelemahannya.</p>
        </div>
      </div>

      {notice && <div className="banner">{notice}</div>}

      <Panel title="Metode" note="Yang datanya belum lengkap tidak bisa dipilih.">
        <form onSubmit={create}>
          <div className="filters">
            <input
              className="input"
              placeholder="Judul analisis"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
            <select className="select" value={method} onChange={(e) => setMethod(e.target.value)} required>
              <option value="">Pilih metode…</option>
              {methods.map((m) => (
                <option key={m.method} value={m.method} disabled={!m.readiness.ok}>
                  {METHOD_LABEL[m.method] ?? m.method}
                  {m.readiness.ok ? '' : ` — belum ada ${m.readiness.missing.join(', ')}`}
                </option>
              ))}
            </select>
            <button className="btn btn-primary" disabled={busy || !method}>
              Buat
            </button>
          </div>

          {(chosen?.needsBaseline || chosen?.needsAsBuilt) && (
            <div className="filters">
              {chosen.needsBaseline && (
                <label className="hint" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  Baseline
                  <select
                    className="select"
                    value={baselineVersionId}
                    onChange={(e) => setBaseline(e.target.value)}
                  >
                    {versions.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.label}
                        {v.isBaseline ? ' · ditandai baseline' : ''}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {chosen.needsAsBuilt && (
                <label className="hint" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  As-built
                  <select
                    className="select"
                    value={asBuiltVersionId}
                    onChange={(e) => setAsBuilt(e.target.value)}
                  >
                    {versions.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <span className="hint">
                As-built adalah versi programme biasa yang kolom aktualnya sudah terisi — bukan
                jenis berkas tersendiri.
              </span>
            </div>
          )}
        </form>

        {chosen && (
          <>
            <p className="panel-foot">{chosen.strength}</p>
            <p className="panel-foot">
              <strong>Kelemahan yang akan tercatat di laporan:</strong> {chosen.limitations}
            </p>
          </>
        )}
      </Panel>
    </>
  );
}
